import React, { useMemo, useState } from 'react';
import { Empty, Flex, Input, Select, Table, Typography } from 'antd';
import { interpolateString } from '@utils/string';
import { CANCEL_BUTTON_LABEL, DELETE_BUTTON_LABEL } from '@constants/common';
import { DEFAULT_PAGE_SIZE } from '@constants/data-page';
import { StatusCodes } from '@constants/enum/common';
import ConfirmationModal from '@components/confirmationModal/ConfirmationModal';
import ActionButtons from '@modules/Roles/components/ActionButtons/ActionButtons';
import CreateRoleButton from '@modules/Roles/components/CreateRoleButton/CreateRoleButton';
import {
  CREATE_ROLE_TEXT,
  EDIT_ROLE_OK_TEXT,
  EDIT_ROLE_TEXT,
  ROLES_CUSTOM_TAB,
  ROLES_DELETE_ROLE_MESSAGE,
  ROLES_DELETE_ROLE_TITLE,
  ROLES_EMPTY_LABEL,
  ROLES_LIST,
  ROLES_SEARCH_EMPTY_LABEL,
  ROLES_SEARCH_PLACEHOLDER,
  ROLES_SORTING_LABEL,
  ROLES_SORTING_OPTIONS,
  ROLES_SORTING_VALUES,
} from '@modules/Roles/constants/roles';
import useRoles from '@modules/Roles/hooks/useRoles';
import { RBACRole } from '@modules/Roles/types/roles';
import styles from './CustomRoles.module.scss';
import PermissionsViewer from '../PermissionsViewer/PermissionsViewer';
import RoleEditorModal from '../RoleEditorModal/RoleEditorModal';

const { Text } = Typography;
const { Search } = Input;

/**
 * CustomRoles Component
 *
 * Note: We use Ant Design's Table component instead of our custom DataTable component
 * because:
 *
 * 1. DataTable is designed for API-based data fetching with complex pagination,
 *    filtering, and sorting that triggers server-side requests
 *
 * 2. This component receives roles data from the parent (via useRoles hook)
 *    and manages search/sorting locally without API calls
 *
 * 3. Using DataTable here would cause infinite API calls since it expects
 *    a getData function for server-side operations, but we're doing client-side
 *    data management
 *
 * 4. Ant Design's Table provides all the features we need (pagination, search,
 *    sorting) with better performance for local data operations
 *
 * If you need to add server-side operations in the future, consider migrating
 * back to DataTable with proper API integration.
 */
const CustomRoles: React.FC = () => {
  const {
    customRoles,
    fetchRolesStatus,
    role,
    setCurrentRole,
    createRole,
    updateRole,
    deleteRole,
  } = useRoles();

  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [createRoleModalOpen, setCreateRoleModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>(ROLES_SORTING_VALUES.DEFAULT);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const filteredAndSortedData = useMemo(() => {
    let filtered = customRoles;

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter(
        (roleItem) =>
          roleItem.role_name.toLowerCase().includes(searchText.toLowerCase()) ||
          roleItem.description?.toLowerCase().includes(searchText.toLowerCase()),
      );
    }

    // Apply sorting
    if (sortOrder !== ROLES_SORTING_VALUES.DEFAULT) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a.role_name.toLowerCase();
        const bValue = b.role_name.toLowerCase();

        if (sortOrder === ROLES_SORTING_VALUES.NAME_ASC) {
          return aValue.localeCompare(bValue);
        } else if (sortOrder === ROLES_SORTING_VALUES.NAME_DESC) {
          return bValue.localeCompare(aValue);
        }
        return 0;
      });
    }

    return filtered;
  }, [customRoles, searchText, sortOrder]);

  const onPreview = (record: RBACRole) => {
    setCurrentRole(record);
    setOpenPreview(true);
  };

  const handleOkInModal = (payload: RBACRole) => {
    if (editMode) {
      const payloadWithId = { ...payload, role_id: role?.role_id };
      updateRole(payloadWithId);
    } else createRole(payload);
    setCreateRoleModalOpen(false);
    setEditMode(false);
  };

  const handleCancelInModal = () => {
    setCreateRoleModalOpen(false);
    setEditMode(false);
    // Reset role to undefined when canceling
    setCurrentRole(undefined);
  };

  const handleEdit = (record: RBACRole) => {
    setCurrentRole(record);
    setEditMode(true);
    setCreateRoleModalOpen(true);
  };

  const handleCreate = () => {
    setEditMode(false);
    // Reset role to undefined when creating
    setCurrentRole(undefined);
    setCreateRoleModalOpen(true);
  };

  const handleClickDelete = (record: RBACRole) => {
    setCurrentRole(record);
    setOpenConfirmationModal(true);
  };

  const handleDelete = () => {
    if (!role) return;
    deleteRole(role);
    setOpenConfirmationModal(false);
    setCurrentRole(undefined);
  };

  const tableColumns = [
    {
      title: ROLES_LIST.ROLE_LABEL,
      dataIndex: 'role',
      key: 'role',
      width: '30%',
      render: (_: unknown, record: RBACRole) => (
        <Text className={styles.customRoles_name}>{record.role_name}</Text>
      ),
    },
    {
      title: ROLES_LIST.DESCRIPTION_LABEL,
      dataIndex: 'description',
      key: 'description',
      render: (_: unknown, record: RBACRole) => (
        <Text className={styles.customRoles_description}>{record.description}</Text>
      ),
    },
    {
      key: 'action',
      width: '8%',
      render: (_: unknown, record: RBACRole) => (
        <ActionButtons
          key={record.role_name}
          onPreview={() => onPreview(record)}
          onEdit={() => handleEdit(record)}
          onDelete={() => handleClickDelete(record)}
        />
      ),
    },
  ];

  return (
    <Flex gap="middle" vertical className={styles.customRoles}>
      <Text>{ROLES_CUSTOM_TAB.TITLE}</Text>

      {customRoles.length > 0 ? (
        <>
          <Flex gap="middle" justify="space-between" align="center">
            <Search
              placeholder={ROLES_SEARCH_PLACEHOLDER}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className={styles.customRoles_search}
              allowClear
            />
            <Flex gap="small" align="center">
              <Text>{ROLES_SORTING_LABEL}</Text>
              <Select
                placeholder={ROLES_SORTING_LABEL}
                value={sortOrder}
                onChange={setSortOrder}
                className={styles.customRoles_sorting}
                options={ROLES_SORTING_OPTIONS.map((option) => ({
                  label: option.label,
                  value: option.key,
                }))}
              />
            </Flex>
          </Flex>

          <Table
            dataSource={filteredAndSortedData}
            columns={tableColumns}
            rowKey="role_name"
            loading={fetchRolesStatus === StatusCodes.IN_PROGRESS}
            pagination={{
              pageSize: DEFAULT_PAGE_SIZE,
              showSizeChanger: true,
              showQuickJumper: true,
              position: ['bottomLeft'],
            }}
            locale={{
              emptyText: (
                // Shown when searching for custom roles and no results are found
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={ROLES_SEARCH_EMPTY_LABEL}
                />
              ),
            }}
          />
        </>
      ) : (
        // Shown when no custom roles have been created yet
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={ROLES_EMPTY_LABEL}>
          <CreateRoleButton onClick={handleCreate} label={CREATE_ROLE_TEXT} />
        </Empty>
      )}

      {openPreview && role && (
        <PermissionsViewer isOpen={openPreview} role={role} onClose={() => setOpenPreview(false)} />
      )}

      {openPreview && role && (
        <PermissionsViewer
          isOpen={openPreview}
          role={role}
          onClose={() => setOpenPreview(false)}
          onEdit={() => handleEdit(role)}
        />
      )}
      <RoleEditorModal
        title={editMode ? EDIT_ROLE_TEXT : CREATE_ROLE_TEXT}
        open={createRoleModalOpen}
        onOk={handleOkInModal}
        onCancel={handleCancelInModal}
        okText={editMode ? EDIT_ROLE_OK_TEXT : CREATE_ROLE_TEXT}
        role={editMode ? role : undefined}
      />
      <ConfirmationModal
        open={openConfirmationModal && !!role}
        onCancel={() => setOpenConfirmationModal(false)}
        onOk={handleDelete}
        onCancelText={CANCEL_BUTTON_LABEL}
        onOkText={DELETE_BUTTON_LABEL}
        title={ROLES_DELETE_ROLE_TITLE}
        message={interpolateString(ROLES_DELETE_ROLE_MESSAGE, {
          roleName: <Text strong>{role?.role_name || ''}</Text>,
        })}
      />
    </Flex>
  );
};

export default CustomRoles;
